import React from 'react';
import { BaseStationTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator, TruncatedText } from '@ais-ui/ui-core';
import { LastOriginDisplay } from './LastOriginDisplay';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

interface IdentificationProps {
  baseStationInfo: BaseStationTargetInfo;
}

const Identification = ({ baseStationInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={baseStationInfo.mmsi}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCountryLabel}
        value={baseStationInfo.country}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseFlagLabel}
        value={baseStationInfo.flag}
      />
      <IdentificationItem
        label={messages.reportsDetailsTypeLabel}
        value={baseStationInfo.targetType}
      />
    </div>
  );
};

interface OperationalStatusProps {
  baseStationInfo: BaseStationTargetInfo;
}

interface OperationalStatusItemProps {
  label: string;
  value: string;
}

const OperationalStatusItem = ({
  label,
  value,
}: OperationalStatusItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

const OperationalStatus = ({ baseStationInfo }: OperationalStatusProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <OperationalStatusItem
        label={messages.shipDetailsBaseLatLabel}
        value={baseStationInfo.latitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLonLabel}
        value={baseStationInfo.longitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLastAISReportLabel}
        value={formatDateTimeWithTimeZone(baseStationInfo.lastAisReport)}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLatitudeLabel}
        value={baseStationInfo.lastAisLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLongitudeLabel}
        value={baseStationInfo.lastAisLongitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLatitudeLabel}
        value={baseStationInfo.lastAisSatLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLongitudeLabel}
        value={baseStationInfo.lastAisSatLongitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatReportLabel}
        value={formatDateTimeWithTimeZone(baseStationInfo.lastAisSatReport)}
      />
      <OperationalStatusItem
        label={messages.shipDetailsVdlLoadLabel}
        value={baseStationInfo.vdlLoad}
      />
    </div>
  );
};

export const BaseStationDetailsPanel = ({
  baseStationInfo,
}: {
  baseStationInfo: BaseStationTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification baseStationInfo={baseStationInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Operational Status</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <OperationalStatus
          baseStationInfo={baseStationInfo}
        ></OperationalStatus>
      </div>
      {baseStationInfo.lastOrigin && (
        <LastOriginDisplay lastOrigin={baseStationInfo.lastOrigin} />
      )}
    </div>
  );
};
